import { useEffect, useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { BankReconSummary, LedgerInspection, inspectLedger, reconcileBank } from '../../api/automationApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the ledger (.xlsx, any nominal ledger layout) and the merged bank statement (.xlsx with Date, Description, Money In, Money Out, Balance).',
  'Check the column mapping table: each ledger field is matched to a column of your file automatically; change any that are wrong. Date, Description and Value are required.',
  'How the Excess sheet columns are named: whatever is in the Ledger Effect column (the account names) is picked up as the column headers. If it is not mapped, the tool finds the other account of each entry from the ledger itself (a line on the same date with the opposite amount). If none is found, the line is left without an account column (only its Value is filled). Matching and the variation do not depend on this. No column is created for the bank account you enter below.',
  'Set the date tolerance and, if needed, the balance per the books (ETB). Type the bank name (the ledger account to reconcile; required).',
  'Click Run Reconciliation. The workbook downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

const money = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function BankReconPanel({ mission, onBack }: PanelProps) {
  const [ledger, setLedger] = useState<File[]>([]);
  const [statement, setStatement] = useState<File[]>([]);
  const [tolerance, setTolerance] = useState('3');
  const [books, setBooks] = useState('');
  const [bankName, setBankName] = useState('');
  const { status, error, result, fail, run } = useFileRun<FileResult<BankReconSummary>>();

  const [inspection, setInspection] = useState<LedgerInspection | null>(null);
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [reading, setReading] = useState(false);
  const [mapError, setMapError] = useState('');

  // read the ledger's headers as soon as it is uploaded and pre-fill the mapping table
  useEffect(() => {
    setInspection(null);
    setMapping({});
    setMapError('');
    if (ledger.length === 0) return;
    let cancelled = false;
    setReading(true);
    inspectLedger(ledger[0])
      .then((info) => {
        if (cancelled) return;
        setInspection(info);
        setMapping(info.mapping);
      })
      .catch((e) => !cancelled && setMapError(e instanceof Error ? e.message : 'Could not read the ledger.'))
      .finally(() => !cancelled && setReading(false));
    return () => {
      cancelled = true;
    };
  }, [ledger]);

  const handleSubmit = async () => {
    if (ledger.length === 0 || statement.length === 0) {
      fail('Upload both the ledger and the bank statement.');
      return;
    }
    if (!bankName.trim()) {
      fail('Enter the bank name (the ledger account to reconcile).');
      return;
    }
    if (!inspection) {
      fail('The ledger columns have not been read yet.');
      return;
    }
    if (!mapping.date || !mapping.description) {
      fail('Map the Date and Description columns.');
      return;
    }
    if (!mapping.value && !(mapping.debit && mapping.credit)) {
      fail('Map the Value column.');
      return;
    }
    const days = parseInt(tolerance, 10);
    if (Number.isNaN(days) || days < 0 || days > 365) {
      fail('Date tolerance must be between 0 and 365 days.');
      return;
    }
    await run(() => reconcileBank(ledger[0], statement[0], {
      ledgerMapping: mapping,
      dateTolerance: days,
      booksClosing: books.trim() === '' ? null : Number(books),
      account: bankName,
    }),'Reconciliation failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <div className="grid gap-4 sm:grid-cols-2">
        <FileDrop
          label="General Ledger"
          required
          accept=".xlsx,.xls"
          hint={mission.maxSizeLabel}
          accentColor={mission.color}
          files={ledger}
          onFiles={setLedger}
        />
        <FileDrop
          label="Bank statement"
          required
          accept=".xlsx,.xls"
          hint={mission.maxSizeLabel}
          accentColor={mission.color}
          files={statement}
          onFiles={setStatement}
        />
      </div>

      {reading && <InfoNotice>Reading the ledger columns…</InfoNotice>}
      {mapError && <ErrorNotice message={mapError} />}
      {inspection && (
        <div className="mb-6 overflow-hidden rounded-lg border border-border">
          <div className="bg-muted px-4 py-2 text-sm font-medium text-foreground">
            Ledger column mapping
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              header found on row {inspection.header_row}
            </span>
          </div>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground">
                <th className="px-4 py-2 font-medium">Field</th>
                <th className="px-4 py-2 font-medium">Column in your file</th>
              </tr>
            </thead>
            <tbody>
              {inspection.fields.filter((f) => !f.advanced).map((f) => {
                const col = mapping[f.key] ?? '';
                return (
                  <tr key={f.key} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 align-top text-foreground">
                      {f.label}
                      {f.required && <span className="text-red-600"> *</span>}
                      {f.hint && <p className="mt-0.5 text-xs text-muted-foreground">{f.hint}</p>}
                    </td>
                    <td className="px-4 py-2 align-top">
                      <select
                        aria-label={f.label}
                        value={col}
                        disabled={status === 'loading'}
                        onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value || null }))}
                        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                      >
                        <option value="">— not in file —</option>
                        {inspection.columns.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mb-6 grid gap-4 rounded-lg border border-border bg-muted p-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label htmlFor="br-bank" className="mb-1.5">
            Bank name <span className="text-red-600">*</span>
          </Label>
          <Input
            id="br-bank"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Required. The ledger account to reconcile (a unique part of its name is enough). No column is created for it on the Excess sheet. If the ledger has no Account name column, the whole file is treated as this account.
          </p>
        </div>
        <div>
          <Label htmlFor="br-tol" className="mb-1.5">
            Date tolerance (days)
          </Label>
          <Input
            id="br-tol"
            type="number"
            min={0}
            max={365}
            value={tolerance}
            onChange={(e) => setTolerance(e.target.value)}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            0 = same day only. Otherwise unmatched lines are paired with the closest day in range.
          </p>
        </div>
        <div>
          <Label htmlFor="br-books" className="mb-1.5">
            Balance as per books (ETB)
          </Label>
          <Input
            id="br-books"
            type="number"
            step="0.01"
            value={books}
            onChange={(e) => setBooks(e.target.value)}
            placeholder="Leave blank to use the ledger balance"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            If blank, the ledger's beginning balance plus movements is used.
          </p>
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Reconciling…' : 'Run Reconciliation'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Reconciling…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
            ✅ {result.filename} downloaded{s ? ` (account: ${s.account})` : ''}.
          </div>
          {s?.messages.map((m, i) => (
            <div
              key={i}
              className={`rounded-lg px-4 py-3 ${
                m.level === 'success' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'
              }`}
            >
              {m.text}
            </div>
          ))}
          {s && (
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <tbody>
                  {Object.entries(s.figures).map(([label, value]) => (
                    <tr key={label} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 text-muted-foreground">{label}</td>
                      <td className="px-4 py-2 text-right font-medium text-foreground">{money(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload the ledger and the bank statement.</InfoNotice>}
    </MissionShell>
  );
}

export default BankReconPanel;
